import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import roleCatalogService from './roleCatalogService';
import permissionService from '../../services/authority/permissionService';
import useCheckPermission from '../../hooks/useCheckPermission';
import PermissionDialog, {
  PermissionOption,
} from '../authority/roles/PermissionDialog';

const RoleCatalogEdit = () => {
  const params = useParams();
  const isCreate = !params.id || params.id === 'new';
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { canPlatform } = useCheckPermission();
  const readonly = !canPlatform(['ManageRoleCatalog']);
  const [entity, setEntity] = useState<any>({
    id: '',
    name: '',
    description: '',
    isPublished: true,
    isSystem: false,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionOption[]>([]);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  useEffect(() => {
    permissionService.list().then((response: any) => {
      const rows = Array.isArray(response)
        ? response
        : response?.records ?? [];
      setAllPermissions(
        rows
          .map((x: any) =>
            typeof x === 'string'
              ? { name: x, description: x }
              : {
                  id: x.id ?? x.Id,
                  name: x.name ?? x.Name,
                  description: x.description ?? x.Description ?? x.name ?? x.Name,
                }
          )
          .filter((p: PermissionOption) => !!p.name)
      );
    });
  }, []);

  useEffect(() => {
    if (isCreate) return;
    dispatch({ type: 'SHOW_QUERY' });
    roleCatalogService
      .listTemplates()
      .then((response) => {
        const found = response.records?.find((x: any) => x.id === params.id);
        if (found) setEntity(found);
      })
      .finally(() => dispatch({ type: 'HIDE' }));

    roleCatalogService.listPermissions(params.id).then((response) => {
      if (!response.hasError) {
        setSelectedPermissions(
          (response.records ?? []).map((x: any) =>
            typeof x === 'string' ? x : x.name ?? x.Name
          )
        );
      }
    });
  }, [params.id]);

  const selectedLabels = useMemo(() => {
    const byName = new Map(
      allPermissions.map((p) => [p.name, p.description || p.name])
    );
    return selectedPermissions.map((name) => byName.get(name) || name);
  }, [allPermissions, selectedPermissions]);

  const onSave = () => {
    dispatch({ type: 'SHOW_SAVE' });
    // Empty string is not a valid Guid — omit id on create so the API can assign one.
    const { id: _id, ...createPayload } = entity;
    const savePromise = isCreate
      ? roleCatalogService.createTemplate(createPayload)
      : roleCatalogService.updateTemplate(entity);

    savePromise
      .then(async (response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
          return;
        }
        const id = response.result?.id ?? params.id;
        if (id) {
          await roleCatalogService.setPermissions(id, selectedPermissions);
        }
        enqueueSnackbar('Mentve', { variant: 'success' });
        navigate('/role-catalog');
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <h2>{isCreate ? 'Új bérlő szerepkör' : 'Bérlő szerepkör szerkesztése'}</h2>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Name"
            value={entity.name ?? ''}
            disabled={readonly || entity.isSystem}
            onChange={(e) => setEntity({ ...entity, name: e.target.value })}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Description"
            value={entity.description ?? ''}
            disabled={readonly}
            onChange={(e) =>
              setEntity({ ...entity, description: e.target.value })
            }
          />
        </Grid>
        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!entity.isPublished}
                disabled={readonly}
                onChange={(e) =>
                  setEntity({ ...entity, isPublished: e.target.checked })
                }
              />
            }
            label="Published"
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Permissions
          </Typography>
          <Button
            variant="outlined"
            disabled={readonly}
            onClick={() => setPermissionDialogOpen(true)}
          >
            Jogosultságok módosítása ({selectedPermissions.length})
          </Button>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedLabels.slice(0, 12).map((label) => (
              <Chip key={label} size="small" label={label} />
            ))}
            {selectedLabels.length > 12 && (
              <Chip
                size="small"
                label={`+${selectedLabels.length - 12} további`}
              />
            )}
          </Box>
          <PermissionDialog
            open={permissionDialogOpen}
            permissions={allPermissions}
            selectedPermissions={selectedPermissions}
            selectionMode="name"
            readonly={readonly}
            title="Szerepkör sablon jogosultságai"
            onClose={(selected) => {
              setSelectedPermissions(selected.map(String));
              setPermissionDialogOpen(false);
            }}
          />
        </Grid>
        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!readonly && (
              <Button variant="contained" onClick={onSave}>
                Mentés
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Mégse
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RoleCatalogEdit;
